import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Header,
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Spinner,
  Badge,
  toast,
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useReportFilters } from '@/hooks/use-report-filters';
import { reportsService } from '@/services/reports-service';
import type { ReportFilters } from '@/types/reports';
import { formatDisplayDate } from '@/lib/date-utils';
import { ReportFiltersBar } from '@/components/reports/report-filters';
import {
  ReportLoadingState,
  ReportErrorState,
  ReportEmptyState,
  ReportAccessDeniedState,
} from '@/components/reports/report-states';
import { ErrorBoundary } from '@/components/reports/error-boundary';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Award,
} from '@/lib/icons';

// ─── Data types ──────────────────────────────────────────────────────────────

interface ExamSessionSummary {
  id: string | number;
  status: string;
  graded_at: string | null;
  overall_correct: number | null;
  overall_total: number | null;
  overall_percent: number | null;
  passed: boolean | null;
  contentful_entry_id: string | null;
}

interface PcacpUserRow {
  user_id: string | number;
  first_name: string;
  last_name: string;
  email: string;
  org_name: string;
  attempt_count: number;
  last_completed_at: string | null;
  sessions: ExamSessionSummary[];
}

interface PcacpExamResultsApiResponse {
  data: PcacpUserRow[];
  meta: { total_count: number; exam_titles: Record<string, string> };
}

// ─── Filter config ────────────────────────────────────────────────────────────

// ─── Sub-components ───────────────────────────────────────────────────────────

function ResultBadge({ session }: { session: ExamSessionSummary }) {
  if (session.status === 'expired') {
    return <Badge variant="outline">Expired</Badge>;
  }
  if (session.status === 'in_progress') {
    return <Badge variant="outline">In Progress</Badge>;
  }
  if (session.status === 'submitted') {
    return <Badge variant="outline">Submitted</Badge>;
  }
  if (session.passed === true) {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200">
        Pass
      </Badge>
    );
  }
  if (session.passed === false) {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200">
        Fail
      </Badge>
    );
  }
  return <Badge variant="outline">{session.status}</Badge>;
}

function UserResultBadge({ sessions }: { sessions: ExamSessionSummary[] }) {
  const gradedSessions = sessions.filter((s) => s.status === 'graded');
  if (gradedSessions.length === 0) {
    const hasExpired = sessions.some((s) => s.status === 'expired');
    return hasExpired ? <Badge variant="outline">Expired</Badge> : <Badge variant="outline">In Progress</Badge>;
  }
  // Show the result of the most recent graded session
  const latest = gradedSessions[0];
  return <ResultBadge session={latest} />;
}

// ─── Summary stats ────────────────────────────────────────────────────────────

function SummaryStats({ rows }: { rows: PcacpUserRow[] }) {
  const totalCandidates = rows.length;

  const allGradedSessions = rows.flatMap((r) =>
    r.sessions.filter((s) => s.status === 'graded')
  );

  const totalAttempts = rows.reduce((sum, r) => sum + r.attempt_count, 0);

  const passCount = allGradedSessions.filter((s) => s.passed === true).length;
  const passRate =
    allGradedSessions.length > 0
      ? Math.round((passCount / allGradedSessions.length) * 100)
      : null;

  const scoredSessions = allGradedSessions.filter((s) => s.overall_percent !== null);
  const avgScore =
    scoredSessions.length > 0
      ? Math.round(
          scoredSessions.reduce((sum, s) => sum + s.overall_percent!, 0) /
            scoredSessions.length
        )
      : null;

  const stats = [
    { label: 'Total Candidates', value: totalCandidates },
    { label: 'Total Evaluation Sessions', value: totalAttempts },
    { label: 'Pass Rate', value: passRate !== null ? `${passRate}%` : '—' },
    { label: 'Avg Score', value: avgScore !== null ? `${avgScore}%` : '—' },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {stats.map((s) => (
        <div key={s.label} className="rounded-lg border bg-card p-4">
          <p className="text-sm text-muted-foreground">{s.label}</p>
          <p className="text-3xl font-bold mt-1">{s.value}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Main report component ────────────────────────────────────────────────────

export function PcacpExamResultsReport() {
  usePageContext({ showPageHeader: false });
  const { data: currentAdmin } = useCurrentUser();

  const adminLevel =
    currentAdmin?.type === 'license_admin'
      ? 'license_admin'
      : (currentAdmin?.level as string | undefined);

  const {
    filters,
    updateFilter,
    updateDateFilter,
    clearFilters,
    hasFilters,
    isAffiliate,
  } = useReportFilters({ currentAdmin });

  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadingSessionId, setDownloadingSessionId] = useState<string | number | null>(null);

  // Prosci Admin only: super, admin, data
  const ALLOWED_LEVELS = ['super', 'admin', 'data'];
  const hasAccess =
    currentAdmin?.type === 'prosci_admin' &&
    !!adminLevel &&
    ALLOWED_LEVELS.includes(adminLevel);

  const { data, isLoading, error } = useQuery<PcacpExamResultsApiResponse>({
    queryKey: ['pcacp-exam-results', filters],
    queryFn: () =>
      reportsService.getPcacpExamResults(filters) as Promise<PcacpExamResultsApiResponse>,
    enabled: hasAccess,
  });

  const rows = data?.data ?? [];
  const examTitles = data?.meta?.exam_titles ?? {};

  // Accumulate exam options so the dropdown stays populated when a filter is active
  const [stableExamOptions, setStableExamOptions] = useState<Record<string, string>>({});
  useEffect(() => {
    const titles = data?.meta?.exam_titles;
    if (titles && Object.keys(titles).length > 0) {
      setStableExamOptions((prev) => ({ ...prev, ...titles }));
    }
  }, [data?.meta?.exam_titles]);

  const filterConfig = useMemo(() => ({
    fields: [
      {
        key: 'search' as keyof ReportFilters,
        type: 'text' as const,
        label: 'Search',
        placeholder: 'Name, email, or organization',
      },
      {
        key: 'exam_entry_id' as keyof ReportFilters,
        type: 'select' as const,
        label: 'Exam',
        placeholder: 'All exams',
        options: Object.entries(stableExamOptions).map(([value, label]) => ({ value, label })),
      },
      {
        key: 'result' as keyof ReportFilters,
        type: 'select' as const,
        label: 'Result',
        placeholder: 'All results',
        options: [
          { value: 'pass', label: 'Pass' },
          { value: 'fail', label: 'Fail' },
          { value: 'expired', label: 'Expired' },
          { value: 'in_progress', label: 'Pending' },
        ],
      },
      {
        key: 'from_date' as keyof ReportFilters,
        type: 'date' as const,
        label: 'Completed From',
        placeholder: 'Select date',
      },
      {
        key: 'to_date' as keyof ReportFilters,
        type: 'date' as const,
        label: 'Completed To',
        placeholder: 'Select date',
      },
    ],
    hasFiltersCheck: (filters: ReportFilters) =>
      Boolean(filters.search || filters.exam_entry_id || filters.result || filters.from_date || filters.to_date),
  }), [stableExamOptions]);

  function toggleUser(userId: string) {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }

  async function handleDownload() {
    setIsDownloading(true);
    try {
      await reportsService.downloadPcacpExamResults(filters);
    } catch {
      toast.error('Failed to download report. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  }

  async function handleSessionDownload(sessionId: string | number) {
    setDownloadingSessionId(sessionId);
    try {
      await reportsService.downloadPcacpExamSession(sessionId);
    } catch {
      toast.error('There was an error fetching the exam report, please try again or contact support.');
    } finally {
      setDownloadingSessionId(null);
    }
  }

  return (
    <ErrorBoundary>
      <Header
        title="PCACP Exam Results"
        size="small"
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/reports">Reports</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>PCACP Exam Results</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      />

      <ReportFiltersBar
        filters={filters}
        filterConfig={filterConfig}
        adminLevel={adminLevel as string}
        isAffiliate={isAffiliate}
        onFilterChange={updateFilter}
        onDateChange={updateDateFilter}
        onClear={clearFilters}
        hasFilters={hasFilters}
      />

      <ContentContainer>
        {!hasAccess ? (
          <ReportAccessDeniedState message="You do not have permission to view this report." />
        ) : error ? (
          <ReportErrorState error={error} />
        ) : isLoading && !data ? (
          <ReportLoadingState title="Loading exam results" />
        ) : rows.length === 0 ? (
          <ReportEmptyState
            icon={<Award />}
            title="No exam results found"
            description="Try adjusting your filters to find PCACP exam results."
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
          />
        ) : (
          <>
            <SummaryStats rows={rows} />
            <Card>
            <CardHeader>
              <CardTitle>PCACP Exam Results</CardTitle>
              <CardDescription>
                PCACP exam attempt history, scores, and pass/fail outcomes by user.
              </CardDescription>
              <CardAction>
                <Button
                  type="button"
                  onClick={handleDownload}
                  disabled={isDownloading}
                  variant="secondary"
                >
                  {isDownloading ? <Spinner size="small" /> : <Download className="h-4 w-4" />}
                  Download Full Report
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8" />
                    <TableHead>User</TableHead>
                    <TableHead>Organization</TableHead>
                    <TableHead className="text-center">Attempts</TableHead>
                    <TableHead>Last Completed</TableHead>
                    <TableHead>Result</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => {
                    const userId = String(row.user_id);
                    const isExpanded = expandedUsers.has(userId);
                    const attemptSessions = row.sessions;

                    return (
                      <>
                        {/* User summary row */}
                        <TableRow
                          key={userId}
                          className={attemptSessions.length > 0 ? 'cursor-pointer hover:bg-muted/50' : undefined}
                          onClick={() => attemptSessions.length > 0 && toggleUser(userId)}
                        >
                          <TableCell className="text-center">
                            {attemptSessions.length > 0 && (
                              isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">
                              {row.first_name} {row.last_name}
                            </div>
                            <div className="text-sm text-muted-foreground">{row.email}</div>
                          </TableCell>
                          <TableCell>{row.org_name}</TableCell>
                          <TableCell className="text-center">{row.attempt_count}</TableCell>
                          <TableCell>
                            {row.last_completed_at
                              ? formatDisplayDate(row.last_completed_at)
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <UserResultBadge sessions={row.sessions} />
                          </TableCell>
                        </TableRow>

                        {/* Expanded attempt rows */}
                        {isExpanded && attemptSessions.length > 0 && (
                          <TableRow key={`${userId}-attempts`}>
                            <TableCell colSpan={6} className="p-0">
                              <div className="bg-muted/30 border-t border-b">
                                <Table>
                                  <TableHeader>
                                    <TableRow className="bg-muted/50">
                                      <TableHead className="w-8" />
                                      <TableHead>Exam</TableHead>
                                      <TableHead>Completed Date</TableHead>
                                      <TableHead className="text-center">Correct</TableHead>
                                      <TableHead className="text-center">Incorrect</TableHead>
                                      <TableHead className="text-center">Total</TableHead>
                                      <TableHead className="text-center">Score</TableHead>
                                      <TableHead>Result</TableHead>
                                      <TableHead />
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {attemptSessions.map((session, idx) => {
                                      const incorrect =
                                        session.overall_total !== null &&
                                        session.overall_correct !== null
                                          ? session.overall_total - session.overall_correct
                                          : null;
                                      return (
                                        <TableRow key={session.id} className="bg-background">
                                          <TableCell className="text-center text-xs text-muted-foreground pl-8">
                                            #{idx + 1}
                                          </TableCell>
                                          <TableCell className="text-sm">
                                            {session.contentful_entry_id
                                              ? (examTitles[session.contentful_entry_id] ?? '—')
                                              : '—'}
                                          </TableCell>
                                          <TableCell>
                                            {session.graded_at
                                              ? formatDisplayDate(session.graded_at)
                                              : '—'}
                                          </TableCell>
                                          <TableCell className="text-center">
                                            {session.overall_correct ?? '—'}
                                          </TableCell>
                                          <TableCell className="text-center">
                                            {incorrect ?? '—'}
                                          </TableCell>
                                          <TableCell className="text-center">
                                            {session.overall_total ?? '—'}
                                          </TableCell>
                                          <TableCell className="text-center">
                                            {session.overall_percent !== null
                                              ? `${session.overall_percent.toFixed(1)}%`
                                              : '—'}
                                          </TableCell>
                                          <TableCell>
                                            <ResultBadge session={session} />
                                          </TableCell>
                                          <TableCell>
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              disabled={session.status !== 'graded' || downloadingSessionId === session.id}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (session.status === 'graded') {
                                                  handleSessionDownload(session.id);
                                                }
                                              }}
                                            >
                                              {downloadingSessionId === session.id ? (
                                                <Spinner size="small" />
                                              ) : (
                                                <Download className="h-3 w-3" />
                                              )}
                                              Download Report
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
            </Card>
          </>
        )}
      </ContentContainer>

    </ErrorBoundary>
  );
}
